import { Component, OnInit, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { QuestionnaireService } from '../../core/services/questionnaire.service';
import { FormDropdownService } from '../../core/services/form-dropdown.service';
import { Questionnaire } from '../../core/models/models';
import { Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { MenuItem, MessageService, ConfirmationService } from 'primeng/api';
import { ColumnConfig } from '../../backoffice/components/dashboard/table-config';
import { RegistryTableComponent } from '../../core/registry-table/registry-table.component';

// Importazioni PrimeNG 2026
import { ButtonModule } from 'primeng/button';
import { Toast } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { escapeHtml } from '../../core/validators/no-special-chars.validator';

@Component({
  selector: 'app-list-questionnaires',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    Toast,
    ConfirmDialogModule,
    BreadcrumbModule,
    ToggleSwitchModule,
    RegistryTableComponent,
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './list-questionnaires.component.html',
  styleUrls: ['./list-questionnaires.component.scss'],
})
export class ListQuestionnairesComponent implements OnInit {
  private titleService = inject(Title);
  private service = inject(QuestionnaireService);
  private formDropdownService = inject(FormDropdownService);
  private router = inject(Router);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);

  // Table configuration
  tableTitle = 'Questionari Attivi';
  tableIcon = 'pi-list-check';
  columns: ColumnConfig[] = [
    { title: 'Nome', field: 'name', sortable: true, filterType: 'text', filterPlaceholder: 'Cerca nome' },
    { title: 'Versione', field: 'version', sortable: true, filterType: 'text', filterPlaceholder: 'Cerca versione' },
    { title: 'Tipo', field: 'type', type: 'tag', sortable: true, filterType: 'select', filterPlaceholder: 'Filtra tipo', 
      filterOptions: [{ label: 'Survey', value: 'SURVEY' }, { label: 'Checklist', value: 'CHECKLIST' }] },
    { title: 'Stato', field: 'status', type: 'tag', sortable: true, filterType: 'select', filterPlaceholder: 'Filtra stato',
      filterOptions: [{ label: 'Attivo', value: 'ATTIVO' }, { label: 'Non Attivo', value: 'NONATTIVO' }] },
    { title: 'Fiduciari', field: 'trusteeTypesLabel', sortable: true, filterType: 'text', filterPlaceholder: 'Cerca fiduciari' },
    { title: 'Ultima Modifica', field: 'updatedAt', type: 'date', sortable: true, filterType: 'date', filterPlaceholder: 'Filtra data' },
    { title: 'Azioni', type: 'action', filterType: 'none' },
  ];
  data = signal<any[]>([]);
  loading = signal<boolean>(false);
  showDeleted = signal<boolean>(false);

  breadcrumbItems = signal<MenuItem[]>([
    { label: 'Questionari' },
  ]);

  ngOnInit(): void {
    // WCAG 9.2.4.2: Set descriptive page title
    this.titleService.setTitle('Gestione Questionari - Portale Fiduciari AXA');
    
    // Pre-carica le categorie fiduciario per decodifica sincrona
    this.formDropdownService.preloadTrusteeCategories().subscribe();
    
    this.loadQuestionnaires();
  }

  private loadQuestionnaires(): void {
    this.loading.set(true);
    const observable = this.showDeleted()
      ? this.service.listQuestionnairesWithDeleted()
      : this.service.listQuestionnaires();

    observable.subscribe({
      next: (questionnaires) => {
        const mappedData = (questionnaires || []).map((q: any) => ({
          ...q,
          version: `v${q.version}`,
          status: this.getQuestionnaireStatus(q.validTo),
          updatedAt: q.updatedAt ? this.parseUpdatedAt(q.updatedAt) : null,
          trusteeTypesLabel: this.decodeTrusteeTypes(q.trusteeTypes || (q.trusteeType ? [q.trusteeType] : [])),
        }));
        this.data.set(mappedData);
        this.loading.set(false);
      },
      error: (error) => {
        console.error('Errore caricamento questionari:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Impossibile caricare i questionari',
          life: 5000,
        });
        this.data.set([]);
        this.loading.set(false);
      },
    });
  }

  // =====================
  // EVENT HANDLERS
  // =====================
  onShowDeletedChange(): void {
    this.loadQuestionnaires();
  }

  onEditClick(rowData: any): void {
    if (rowData?.id) {
      this.router.navigate(['/create-questionnaire', rowData.id]);
    }
  }

  onDeleteClick(rowData: any): void {
    if (!rowData?.id) return;

    this.confirmationService.confirm({
      message: `Sei sicuro di voler eliminare il questionario "${escapeHtml(rowData.name)}"?`,
      header: 'Conferma Eliminazione',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Elimina',
      rejectLabel: 'Annulla',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-text',
      accept: () => {
        this.service.deleteQuestionnaire(rowData.id).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Questionario eliminato',
              detail: `Il questionario "${rowData.name}" è stato eliminato con successo`,
              life: 3000,
            });
            this.loadQuestionnaires();
          },
          error: (error) => {
            console.error('Errore eliminazione questionario:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Errore',
              detail: 'Impossibile eliminare il questionario',
              life: 5000,
            });
          },
        });
      },
    });
  }

  onExport(): void {
    console.log('Export questionari');
    // TODO: Implementare export
    this.messageService.add({
      severity: 'info',
      summary: 'Export',
      detail: 'Funzionalità in fase di sviluppo',
      life: 3000,
    });
  }

  onClearFilters(): void {
    console.log('Filtri azzerati');
  }

  private parseUpdatedAt(value: string): Date {
    const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(value);
    return new Date(hasTimezone ? value : `${value}Z`);
  }

  private getQuestionnaireStatus(validTo: string | Date): string {
    const today = new Date().toISOString().slice(0, 10);
    const validToStr = validTo instanceof Date ? validTo.toISOString().slice(0, 10) : validTo;
    return validToStr >= today ? 'ATTIVO' : 'NONATTIVO';
  }

  private decodeTrusteeTypes(trusteeTypes: string[]): string {
    return trusteeTypes
      .map((trusteeType) => this.formDropdownService.decodeProfessionalTypeSync(trusteeType))
      .join(', ');
  }
}
